import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { Dashboard } from 'app/analytics/dashboards/dashboard';
import { FormService } from 'app/form/form.service';
import { ChartSettings } from 'app/shared/model/charts/chart-settings.model';
import { HttpClientService } from 'app/shared/service/http-client.service';
import { SampleChartDataService } from 'app/shared/service/sample-chart-data.service';

@Injectable()
export class AnalyticsService {
  lastVisitedDashboardId;

  constructor(
    private readonly http: HttpClientService,
    private readonly sampleChartDataService: SampleChartDataService,
    private readonly formService: FormService,
  ) {}

  getTeamDashboards(teamId: string): Observable<Dashboard[]> {
    return this.http.get(`teams/${teamId}/dashboards`);
  }

  getCompanyDashboards(companyId: string): Observable<Dashboard[]> {
    return this.http.get(`companies/${companyId}/dashboards`);
  }

  addTeamDashboard(teamId: string, dashboardName: string): Observable<Dashboard> {
    return this.http.post(`teams/${teamId}/dashboards`, { name: dashboardName });
  }

  addCompanyDashboard(companyId: string, dashboardName: string): Observable<Dashboard> {
    return this.http.post(`companies/${companyId}/dashboards`, { name: dashboardName });
  }

  updateTeamDashboard(teamId: string, dashboardId: string, dashboardName: string): Observable<Dashboard> {
    return this.http.put(`teams/${teamId}/dashboards/${dashboardId}`, { name: dashboardName });
  }

  updateCompanyDashboard(
    companyId: string,
    dashboardId: string,
    dashboardName: string,
  ): Observable<Dashboard> {
    return this.http.put(`companies/${companyId}/dashboards/${dashboardId}`, { name: dashboardName });
  }

  deleteTeamDashboard(teamId: string, dashboardId: string): Observable<Dashboard> {
    return this.http.remove(`teams/${teamId}/dashboards/${dashboardId}`);
  }

  deleteCompanyDashboard(companyId: string, dashboardId: string): Observable<Dashboard> {
    return this.http.remove(`companies/${companyId}/dashboards/${dashboardId}`);
  }

  getTeamDashboardCharts(teamId: string, dashboardId: string): Observable<ChartSettings[]> {
    return this.http.get(`teams/${teamId}/dashboards/${dashboardId}/charts`).pipe(
      map((charts) => {
        return charts.map((chart) => {
          if (chart.isSampleChart) {
            chart.template = this.sampleChartDataService.getSampleTemplate();
          }
          return chart;
        });
      }),
    );
  }

  getCompanyDashboardCharts(companyId: string, dashboardId: string): Observable<ChartSettings[]> {
    return this.http.get(`companies/${companyId}/dashboards/${dashboardId}/charts`).pipe(
      map((charts) => {
        return charts.map((chart) => {
          if (chart.isSampleChart) {
            chart.template = this.sampleChartDataService.getSampleTemplate();
          }
          return chart;
        });
      }),
    );
  }

  addTeamChart(teamId: string, dashboardId: string, chartConfig): Observable<ChartSettings> {
    return this.http.post(`teams/${teamId}/dashboards/${dashboardId}/charts`, chartConfig);
  }

  addCompanyChart(companyId: string, dashboardId: string, chartConfig): Observable<ChartSettings> {
    return this.http.post(`companies/${companyId}/dashboards/${dashboardId}/charts`, chartConfig);
  }

  updateTeamChart(
    teamId: string,
    dashboardId: string,
    chartId: string,
    chartConfig,
  ): Observable<ChartSettings> {
    return this.http.put(`teams/${teamId}/dashboards/${dashboardId}/charts/${chartId}`, chartConfig).pipe(
      map((chart) => {
        if (chart.isSampleChart) {
          chart.template = this.sampleChartDataService.getSampleTemplate();
        }
        return chart;
      }),
    );
  }

  updateCompanyChart(
    companyId: string,
    dashboardId: string,
    chartId: string,
    chartConfig,
  ): Observable<ChartSettings> {
    return this.http
      .put(`companies/${companyId}/dashboards/${dashboardId}/charts/${chartId}`, chartConfig)
      .pipe(
        map((chart) => {
          if (chart.isSampleChart) {
            chart.template = this.sampleChartDataService.getSampleTemplate();
          }
          return chart;
        }),
      );
  }

  deleteTeamChart(teamId: string, dashboardId: string, chartId: string) {
    return this.http.remove(`teams/${teamId}/dashboards/${dashboardId}/charts/${chartId}`);
  }

  deleteCompanyChart(companyId: string, dashboardId: string, chartId: string) {
    return this.http.remove(`companies/${companyId}/dashboards/${dashboardId}/charts/${chartId}`);
  }

  getTeamChart(teamId: string, dashboardId: string, chartId: string): Observable<ChartSettings> {
    return this.http.get<ChartSettings>(`teams/${teamId}/dashboards/${dashboardId}/charts/${chartId}`).pipe(
      map((chart) => {
        if (chart.isSampleChart) {
          chart.template = this.sampleChartDataService.getSampleTemplate();
        }
        return chart;
      }),
    );
  }

  getCompanyChart(companyId: string, dashboardId: string, chartId: string): Observable<ChartSettings> {
    return this.http.get(`companies/${companyId}/dashboards/${dashboardId}/charts/${chartId}`).pipe(
      map((chart) => {
        if (chart.isSampleChart) {
          chart.template = this.sampleChartDataService.getSampleTemplate();
        }
        return chart;
      }),
    );
  }

  getCompanyChartQuantity(
    companyId: string,
    dashboardId: string,
    chartId: string,
  ): Observable<{ totalQuantity: number }> {
    return this.http.get(`companies/${companyId}/dashboards/${dashboardId}/charts/${chartId}/totalQuantity`);
  }

  getTeamChartQuantity(
    teamId: string,
    dashboardId: string,
    chartId: string,
  ): Observable<{ totalQuantity: number }> {
    return this.http.get(`teams/${teamId}/dashboards/${dashboardId}/charts/${chartId}/totalQuantity`);
  }

  getCompanyChartTotalForms(
    companyId: string,
    dashboardId: string,
    chartId: string,
    params: Record<string, any>,
  ): Observable<{ totalForms: number }> {
    return this.http.get(
      `companies/${companyId}/dashboards/${dashboardId}/charts/${chartId}/totalForms`,
      params,
    );
  }

  getTeamChartTotalForms(
    teamId: string,
    dashboardId: string,
    chartId: string,
    params: Record<string, any>,
  ): Observable<{ totalForms: number }> {
    return this.http.get(`teams/${teamId}/dashboards/${dashboardId}/charts/${chartId}/totalForms`, params);
  }

  getFormItemTableColumnValues(templateId, itemId, columnId) {
    if (templateId === this.sampleChartDataService.templateId) {
      return of(this.sampleChartDataService.listItems);
    }

    return this.formService.getFormItemTableColumnValues(templateId, itemId, columnId);
  }

  getFormUsers(templateId) {
    if (templateId === this.sampleChartDataService.templateId) {
      return of(this.sampleChartDataService.users);
    }

    return this.formService.getFormUsers(templateId);
  }

  listTemplateForms(templateId: string, params: Record<string, any>) {
    if (templateId === this.sampleChartDataService.templateId) {
      return of(this.sampleChartDataService.getSampleForms(params));
    }

    return this.formService.listTemplateForms(templateId, { ...params });
  }
}
